/**
 * Alarm sound — the audible half of a raised safety alarm.
 *
 * Until now the only alerting was a visual banner plus a haptic buzz. A silent
 * alarm is nearly as useless as no alarm, so this makes an actual noise: a loud
 * siren that repeats while a priority alarm is standing, so it carries across a
 * cockpit and through wind. It's the FOREGROUND alerting path (app on screen).
 * When the phone is locked or the app is backgrounded the WebView's JS is frozen
 * and this can't run, so the Android local-notification sound covers that case
 * instead (see src/notify).
 *
 * Each alarm can be given its own tone and volume (App & Account → Alarm sound):
 * the runner works out the highest-priority alarm currently firing and asks for
 * ITS tone at ITS volume, so a dragging anchor and a low battery don't sound the
 * same. This module stays dumb about which alarm is which; it just plays the
 * spec it's handed. The tones themselves live in alarmTones.ts.
 *
 * The tone is synthesised with the Web Audio API rather than shipping an audio
 * file, so there's no asset to bundle or fail to load. It degrades cleanly: where
 * there's no AudioContext (older WebView, the test environment) nothing throws,
 * there's just no sound. It's gated by the user's master Alarm-sound toggle (App
 * & Account, default on), mirroring how haptics are gated, so this module never
 * has to know about the settings store.
 */
import { toneById, MOB_CONFIRM_TONE, type ToneId, type ToneNote } from './alarmTones';
import { useAlarmsStore } from '@/store/alarms';

/** Whether alarm sound is on. Default on; the settings store flips this. */
let enabled = true;

/** A tone id plus a volume (0..1) — what one alarm wants played. */
export interface AlarmSoundSpec {
  tone: ToneId;
  /** 0..1, scales the peak gain. Clamped when applied. */
  volume: number;
}

/** The default spec: the classic two-tone siren at full volume. */
export const DEFAULT_ALARM_SPEC: AlarmSoundSpec = { tone: 'siren', volume: 1 };

/** A resolved burst handed to the beep sink: the actual notes plus the volume. */
export interface ResolvedBurst {
  notes: ToneNote[];
  volume: number;
}

/**
 * A beep sink: play one resolved burst NOW. Abstracted (like the haptics vibrate
 * sink) so the runner logic is testable without a real AudioContext, and so an
 * unsupported platform is a clean no-op.
 */
type BeepFn = (burst: ResolvedBurst) => void;

/** The repeating-beep timer while an alarm stands, or null when silent. */
let loopTimer: ReturnType<typeof setInterval> | null = null;

/** The spec currently looping, so a same-spec re-render is a no-op but a changed
 *  spec (a higher alarm took over) swaps the tone rather than being ignored. */
let currentSpec: AlarmSoundSpec = DEFAULT_ALARM_SPEC;

/** Milliseconds between repeats of the siren while an alarm is standing. */
const REPEAT_MS = 1600;

/**
 * Lazily-created shared AudioContext. Created on first use (ideally off a user
 * gesture, which by the time any alarm arms has long since happened), and resumed
 * if the platform suspended it. Kept module-global so every beep reuses the one
 * context rather than churning them.
 */
type MinimalAudioContext = {
  readonly state: string;
  readonly currentTime: number;
  readonly destination: unknown;
  resume: () => Promise<void>;
  createOscillator: () => {
    type: string;
    frequency: {
      setValueAtTime: (v: number, t: number) => void;
      linearRampToValueAtTime: (v: number, t: number) => void;
    };
    connect: (dst: unknown) => void;
    start: (t: number) => void;
    stop: (t: number) => void;
  };
  createGain: () => {
    gain: {
      setValueAtTime: (v: number, t: number) => void;
      linearRampToValueAtTime: (v: number, t: number) => void;
    };
    connect: (dst: unknown) => void;
  };
};

let audioCtx: MinimalAudioContext | null = null;

/** Resolve the AudioContext constructor, or null when the platform has none. */
function resolveAudioContextCtor(): (new () => MinimalAudioContext) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: new () => MinimalAudioContext;
    webkitAudioContext?: new () => MinimalAudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Get (creating on first call) the shared AudioContext, or null if unsupported. */
function getAudioContext(): MinimalAudioContext | null {
  if (audioCtx) return audioCtx;
  const Ctor = resolveAudioContextCtor();
  if (!Ctor) return null;
  try {
    audioCtx = new Ctor();
  } catch {
    audioCtx = null;
  }
  return audioCtx;
}

/** Clamp a volume into 0..1 so a bad saved value can't blow out or silence it. */
function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Schedule one burst on the shared context: each note is a short shaped pulse
 * (quick attack to a peak, decay to near-zero) so it's crisp rather than a click.
 * Peak gain is 0.9 at full volume because this is a safety alert, scaled by the
 * alarm's chosen volume.
 */
function webAudioBeep(burst: ResolvedBurst): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  // A suspended context (autoplay policy) won't make noise; nudge it awake. This
  // succeeds silently once the app has had any user gesture, which it always has
  // by the time an alarm can arm.
  if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);

  const now = ctx.currentTime;
  const peak = 0.9 * clampVolume(burst.volume);
  // A silent burst (volume 0) is nothing to schedule.
  if (peak <= 0) return;
  for (const n of burst.notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = n.wave ?? 'square';
    osc.frequency.setValueAtTime(n.freq, now + n.at);
    // A gliding note (sweep/siren slide) ramps its pitch across the note.
    if (n.toFreq != null) {
      osc.frequency.linearRampToValueAtTime(n.toFreq, now + n.at + n.dur);
    }
    // Shape the pulse: quick attack up to the peak, then decay to near-zero so
    // there's no click when it stops.
    gain.gain.setValueAtTime(0.0001, now + n.at);
    gain.gain.linearRampToValueAtTime(peak, now + n.at + 0.02);
    gain.gain.linearRampToValueAtTime(0.0001, now + n.at + n.dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + n.at);
    osc.stop(now + n.at + n.dur + 0.02);
  }
}

let beepImpl: BeepFn | null = resolveAudioContextCtor() ? webAudioBeep : null;

/** Resolve a spec's tone id into the actual notes plus its clamped volume. */
function resolveBurst(spec: AlarmSoundSpec): ResolvedBurst {
  return { notes: toneById(spec.tone).notes, volume: clampVolume(spec.volume) };
}

/** Whether two specs are the same tone and volume (so a re-render is a no-op). */
function sameSpec(a: AlarmSoundSpec, b: AlarmSoundSpec): boolean {
  return a.tone === b.tone && a.volume === b.volume;
}

/** Turn alarm sound on or off (driven by the user's master Alarm-sound toggle). */
export function setAlarmSoundEnabled(on: boolean): void {
  enabled = on;
  // Killing the toggle mid-alarm should shut it up immediately.
  if (!on) stopAlarmSound();
}

/** Whether alarm sound is currently enabled. */
export function isAlarmSoundEnabled(): boolean {
  return enabled;
}

/** Play a single burst now for the given spec. Returns true only when requested. */
export function playAlarmBurst(spec: AlarmSoundSpec = currentSpec): boolean {
  if (!enabled) return false;
  const fn = beepImpl;
  if (!fn) return false;
  try {
    fn(resolveBurst(spec));
    return true;
  } catch {
    return false;
  }
}

/**
 * Preview a tone once, regardless of the master toggle, so the settings screen
 * can audition a sound even with alarm sound turned off. Still a clean no-op
 * where there's no audio.
 */
export function previewAlarmTone(spec: AlarmSoundSpec): void {
  const fn = beepImpl;
  if (!fn) return;
  try {
    fn(resolveBurst(spec));
  } catch {
    /* ignore */
  }
}

/**
 * Play the MOB (man overboard) confirmation once: the rising "got it" three-note
 * (MOB_CONFIRM_TONE), at FULL volume, over the top of whatever else is sounding.
 * It's fired the instant a MOB is dropped, alongside the mob haptic, so the crew
 * KNOW the mark registered even in the chaos of someone going over the side.
 *
 * It deliberately does NOT stop or duck a standing alarm: a real hazard (shallow
 * water, a closing target) must keep sounding while the MOB is handled. Instead it
 * plays at full volume with a rising, positive contour so it cuts through and
 * reads as a confirmation rather than one more warning. It's a single burst, not a
 * loop, so it never stacks a timer or overlaps itself.
 *
 * Gated like every other audible path: silent when the global Alarm-sound toggle
 * is off OR master mute is on. That's the one place the module reads the live
 * master-mute flag (runtime alarm state, not a saved setting), so MOB gating lives
 * in a single spot and both trigger sites stay one-liners. Even when silenced the
 * mob haptic still fires, so there's still a physical confirmation. A clean no-op
 * where there's no audio.
 */
export function playMobConfirmation(): boolean {
  if (!enabled) return false;
  if (useAlarmsStore.getState().masterMuted) return false;
  const fn = beepImpl;
  if (!fn) return false;
  try {
    fn({ notes: MOB_CONFIRM_TONE.notes, volume: 1 });
    return true;
  } catch {
    return false;
  }
}

/**
 * Start the repeating siren for a standing alarm with the given spec: one burst
 * immediately, then a fresh burst every REPEAT_MS until stopAlarmSound(). Passing
 * the SAME spec while already sounding is a no-op, so re-renders don't stack
 * timers; passing a DIFFERENT spec (a higher-priority alarm has taken over) swaps
 * the tone straight away. Does nothing (cleanly) when disabled or unsupported.
 */
export function startAlarmSound(spec: AlarmSoundSpec = DEFAULT_ALARM_SPEC): void {
  if (!enabled) return;
  if (!beepImpl) return;
  if (loopTimer !== null) {
    if (sameSpec(spec, currentSpec)) return;
    // A different alarm now leads: drop the old timer and re-arm with the new
    // tone so the ear hears the change.
    clearInterval(loopTimer);
    loopTimer = null;
  }
  currentSpec = spec;
  playAlarmBurst(currentSpec);
  loopTimer = setInterval(() => playAlarmBurst(currentSpec), REPEAT_MS);
}

/** Stop the repeating siren. Safe to call when nothing is sounding. */
export function stopAlarmSound(): void {
  if (loopTimer !== null) {
    clearInterval(loopTimer);
    loopTimer = null;
  }
}

/** Whether the repeating siren is currently running. */
export function isAlarmSounding(): boolean {
  return loopTimer !== null;
}

/**
 * Test seam: replace the beep sink (pass null to simulate an unsupported
 * platform). Not used in app code.
 */
export function __setBeepForTest(fn: BeepFn | null): void {
  stopAlarmSound();
  beepImpl = fn;
}

/** Test seam: restore the real platform beep resolution and re-enable. */
export function __resetAlarmSoundForTest(): void {
  stopAlarmSound();
  audioCtx = null;
  beepImpl = resolveAudioContextCtor() ? webAudioBeep : null;
  enabled = true;
  currentSpec = DEFAULT_ALARM_SPEC;
}
